import {Injectable, signal} from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { User } from '../../interfaces/user.interface';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  public showAuthErrorPage = signal(false);

  /**
   * True while a browser redirect to the identity provider is pending.
   * The redirect (window.location.assign) is not instant, so without this flag
   * the app would render the Start Page (folder bar + information message) for a
   * few frames before the browser actually leaves, causing a visible flicker.
   */
  public isRedirecting = signal(false);

  private static readonly KEY_ENTER_URL = 'ENTER_URL';

  baseUrl: string = environment.baseUrl;
  authenticationURL: string = this.baseUrl + '/authorize';

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    withCredentials: true,
  };

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  checkAuthentication(): Observable<User> {
    // The Azure AD authorization code is now delivered to the backend via
    // form_post (POST /login/callback), which performs the token exchange,
    // creates the session, and redirects the browser back here without a
    // ?code= parameter. So on the frontend we simply try to get the logged-in
    // user. If that fails with 401, the app initializer triggers a redirect to
    // Azure AD (see app.config.ts).
    this.rememberInitialURL();
    return this.getLoggedInUser();
  }

  private apiUrl(path: string): string {
    const base = environment.baseUrl || '';
    const b = base.endsWith('/') ? base.slice(0, -1) : base;
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${b}${p}`;
  }

  public redirectToAzureAd(state?: string) {
    // Keep showing the loading indicator (instead of the Start Page) until the
    // browser leaves for the identity provider, so the folder bar and information
    // message don't flicker while the redirect is still pending.
    this.isRedirecting.set(true);
    // We always redirect to start page, because remembering the entered url
    // and redirecting to it after the login, has been moved to the frontend
    // and is now saved in browser session storage.
    const stateParam = encodeURIComponent(state ?? "/");
    window.location.assign(this.apiUrl("/authenticate/loginurl?state=" + stateParam));
  }

  public redirectToAuthErrorPage() {
    // Clear the redirect flag so the error page is shown instead of the loading
    // indicator (e.g. when the SSO token exchange fails after we started login).
    this.isRedirecting.set(false);
    this.showAuthErrorPage.set(true);
    this.router.navigate(["error", "401"]).then();
  }

  private getLoggedInUser(): Observable<User> {
    return this.http.get<User>(this.authenticationURL, this.httpOptions);
  }

  private rememberInitialURL() {
    sessionStorage.setItem(AuthService.KEY_ENTER_URL, window.location.href.replace(window.location.origin,''));
  }
}
